import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  IndexTable,
  Filters,
  Button,
  Badge,
  Text,
  InlineStack,
  BlockStack,
  Pagination,
  EmptyState,
  Spinner,
  Toast,
  Select,
  TextField,
  Tooltip,
  ChoiceList
} from '@shopify/polaris';

// Import services and constants
import { ordersService } from '../services/ordersService';
import { authService } from '../services/authService';
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  ORDER_PRIORITY_LABELS,
  FILTER_OPTIONS,
  PAGINATION_DEFAULTS,
  orderUtils
} from '../constants/orderConstants';
import { useWebSocket } from '../hooks/useWebSocket';
import ImportShopifyOrderModal from '../components/modals/ImportShopifyOrderModal';
import QuickStatsCards from '../components/QuickStatsCards';
import {
  DEFAULT_ORDER_START_DATE,
  createDefaultOrderFilters,
  readOrderFiltersFromQuery,
  writeOrderFiltersToQuery
} from '../utils/orderFiltersQuery';
import { getPaymentDisplay } from '../utils/paymentStatus';

const createShopifyImportRequestId = () => {
  const random = window.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `shopify-import:${random}`;
};

const OrdersPage = ({ fixedSource, excludeSource }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGINATION_DEFAULTS.PAGE_SIZE,
    total: 0,
    totalPages: 0
  });

  // Quick stats state
  const [quickStats, setQuickStats] = useState(null);
  const [quickStatsLoading, setQuickStatsLoading] = useState(true);

  // Filter state - Enhanced with all new filter options
  const [filters, setFilters] = useState(
    () => readOrderFiltersFromQuery(searchParams, fixedSource)
  );
  const [searchInput, setSearchInput] = useState(() => filters.search);
  const [dateFilterTarget, setDateFilterTarget] = useState(null);
  const [selectedStatKey, setSelectedStatKey] = useState(null);

  useEffect(() => {
    setDateFilterTarget(document.getElementById('orders-date-filter-slot'));
  }, []);

  // Keep the latest Orders view in the URL so browser navigation restores it.
  useEffect(() => {
    const nextSearchParams = writeOrderFiltersToQuery(
      searchParams,
      { ...filters, search: searchInput },
      fixedSource
    );
    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [filters, fixedSource, searchInput, searchParams, setSearchParams]);

  // UI state
  const [toast, setToast] = useState(null);

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importRequestId, setImportRequestId] = useState(null);
  const [importTerminal, setImportTerminal] = useState(null);
  const importCountry = authService.getActiveCountry();
  const importStore = importCountry;

  // Create order modal state

  // WebSocket integration for real-time updates
  const {
    isConnected,
    addEventListener,
    removeEventListener
  } = useWebSocket({
    autoConnect: true,
    subscriptions: ['all_orders'],
    onError: (error) => {
      // console.error('WebSocket error:', error);
      // setToast({
      //   content: 'Real-time updates temporarily unavailable',
      //   error: true
      // });
    }
  });

  // Event listeners for actions from App.js
  useEffect(() => {
    const handleRefresh = () => {
      loadOrders();
    };

    const handleImportShopify = () => {
      setImportModalOpen(true);
      setImportError(null);
      setImportTerminal(null);
      setImportRequestId(createShopifyImportRequestId());
    };

    const handleSyncBosta = async () => {
      try {
        const result = await ordersService.syncBostaStatus();

        if (result.success) {
          setToast({
            content: `Synced ${result.updated} of ${result.checked} orders to shipped status`,
            duration: 5000
          });

          // Refresh the orders list to show updated statuses
          loadOrders();
        } else {
          setToast({
            content: result.error || 'Failed to sync Bosta status',
            error: true,
            duration: 5000
          });
        }

      } catch (error) {
        console.error('Error syncing Bosta status:', error);
        setToast({
          content: error.message || 'Failed to sync Bosta status',
          error: true,
          duration: 5000
        });
      }
    };

    window.addEventListener('ordersRefresh', handleRefresh);
    window.addEventListener('ordersImportShopify', handleImportShopify);
    window.addEventListener('ordersSyncBostaStatus', handleSyncBosta);

    return () => {
      window.removeEventListener('ordersRefresh', handleRefresh);
      window.removeEventListener('ordersImportShopify', handleImportShopify);
      window.removeEventListener('ordersSyncBostaStatus', handleSyncBosta);
    };
  }, []);

  // Load orders with current filters and pagination
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);

      // Prepare API filters
      const apiFilters = {
        // Pagination
        page: pagination.page,
        limit: pagination.pageSize,
        // Sorting
        sort: filters.sort
      };

      // Search
      if (filters.search && filters.search.trim()) {
        apiFilters.search = filters.search.trim();
      }

      // Multi-value filters (arrays) - only add if not empty
      if (filters.statuses && filters.statuses.length > 0) {
        apiFilters.statuses = filters.statuses;
      }

      if (filters.priorities && filters.priorities.length > 0) {
        apiFilters.priorities = filters.priorities;
      }

      if (filters.sources && filters.sources.length > 0) {
        apiFilters.sources = filters.sources;
      } else if (excludeSource) {
        apiFilters.exclude_sources = [excludeSource];
      }

      if (filters.flag_types && filters.flag_types.length > 0) {
        apiFilters.flag_types = filters.flag_types;
      }

      // Single value filters - only add if not empty
      if (filters.has_flags) {
        apiFilters.has_flags = filters.has_flags;
      }

      if (filters.payment_method) {
        apiFilters.payment_method = filters.payment_method;
      }

      if (filters.payment_status) {
        apiFilters.payment_status = filters.payment_status;
      }

      if (filters.financial_status) {
        apiFilters.financial_status = filters.financial_status;
      }

      // Date range filters
      if (filters.date_range && filters.date_range !== '' && filters.date_range !== 'custom') {
        apiFilters.date_range = filters.date_range;
      } else if (filters.start_date || filters.end_date) {
        if (filters.start_date) {
          apiFilters.start_date = filters.start_date;
        }
        if (filters.end_date) {
          apiFilters.end_date = filters.end_date;
        }
      }

      const response = await ordersService.getOrders(apiFilters);

      // Handle the actual API response structure: { success: true, data: [...] }
      const ordersData = response.data || response.orders || [];
      setOrders(ordersData);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || ordersData.length,
        totalPages: response.pagination?.totalPages || Math.ceil(ordersData.length / pagination.pageSize)
      }));

      // Store orders data in localStorage for App.js subtitle
      localStorage.setItem('ordersData', JSON.stringify({
        totalOrders: response.pagination?.total || ordersData.length
      }));

    } catch (error) {
      console.error('Error loading orders:', error);
      setToast({
        content: `Failed to load orders: ${error.message}`,
        error: true
      });
    } finally {
      setLoading(false);
    }
  }, [excludeSource, filters, pagination.page, pagination.pageSize]);

  // Load quick statistics for dashboard
  const loadQuickStats = useCallback(async () => {
    try {
      setQuickStatsLoading(true);
      const stats = await ordersService.getQuickStats(
        'all',
        fixedSource || null,
        fixedSource ? null : excludeSource,
        {
          date_range: filters.date_range && filters.date_range !== 'custom'
            ? filters.date_range
            : null,
          start_date: filters.date_range === 'custom' ? filters.start_date : null,
          end_date: filters.date_range === 'custom' ? filters.end_date : null
        }
      );
      setQuickStats(stats);
    } catch (error) {
      console.error('Error loading quick stats:', error);
      // Don't show toast for stats error - non-critical
    } finally {
      setQuickStatsLoading(false);
    }
  }, [
    excludeSource,
    filters.date_range,
    filters.end_date,
    filters.start_date,
    fixedSource
  ]);

  // Handle quick stat card click - filter orders by clicked stat
  const handleStatClick = useCallback((statKey, filterValue) => {
    if (selectedStatKey === statKey) {
      setFilters(prev => ({
        ...prev,
        statuses: [],
        has_flags: ''
      }));
      setSelectedStatKey(null);
      setPagination(prev => ({ ...prev, page: 1 }));
      return;
    }

    if (statKey === 'flagged') {
      // Filter by has_flags
      setFilters(prev => ({
        ...prev,
        has_flags: 'true', // Use string 'true' to match FILTER_OPTIONS
        statuses: [] // Clear status filter
      }));
    } else {
      // Lifecycle cards always filter by their canonical status.
      setFilters(prev => ({
        ...prev,
        statuses: filterValue,
        has_flags: ''
      }));
    }
    setSelectedStatKey(statKey);
    // Reset to first page when filtering
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [selectedStatKey]);

  // WebSocket event handlers for real-time updates
  useEffect(() => {
    if (!isConnected) return;

    const handleOrderUpdate = (data) => {
      setOrders(prevOrders => {
        const updatedOrders = [...prevOrders];
        const orderIndex = updatedOrders.findIndex(order =>
          (order.order_id || order.id || order._id) === data.order_id
        );

        if (orderIndex !== -1) {
          // Update existing order
          updatedOrders[orderIndex] = { ...updatedOrders[orderIndex], ...data.order };
          setToast({
            content: `Order #${data.order_id} has been updated`,
            duration: 3000
          });
        } else {
          // New order - refresh the list if on first page
          if (pagination.page === 1) {
            loadOrders();
          }
        }

        return updatedOrders;
      });
    };

    const handleStatusChange = (data) => {
      setOrders(prevOrders => {
        const updatedOrders = [...prevOrders];
        const orderIndex = updatedOrders.findIndex(order =>
          (order.order_id || order.id || order._id) === data.order_id
        );

        if (orderIndex !== -1) {
          updatedOrders[orderIndex] = {
            ...updatedOrders[orderIndex],
            status: data.new_status,
            updated_at: data.timestamp
          };

          setToast({
            content: `Order #${data.order_id} status changed to ${orderUtils.getStatusLabel(data.new_status)}`,
            duration: 3000
          });
        }

        return updatedOrders;
      });
    };

    // Add event listeners
    addEventListener('orderUpdated', handleOrderUpdate);
    addEventListener('orderStatusChanged', handleStatusChange);

    return () => {
      // Remove event listeners
      removeEventListener('orderUpdated', handleOrderUpdate);
      removeEventListener('orderStatusChanged', handleStatusChange);
    };
  }, [isConnected, pagination.page, addEventListener, removeEventListener, loadOrders]);

  // Load orders on component mount and when dependencies change
  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Load quick stats on component mount
  useEffect(() => {
    loadQuickStats();
  }, [loadQuickStats]);

  // Refresh quick stats when orders list changes (after updates, new orders, etc.)
  useEffect(() => {
    // Debounce the stats refresh to avoid too many calls
    const timer = setTimeout(() => {
      if (!loading && orders.length > 0) {
        loadQuickStats();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [orders.length, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter changes
  const handleFiltersChange = useCallback((newFilters) => {
    if (
      Object.prototype.hasOwnProperty.call(newFilters, 'statuses')
      || Object.prototype.hasOwnProperty.call(newFilters, 'has_flags')
    ) {
      setSelectedStatKey(null);
    }
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  const handleDateRangeChange = useCallback((dateRange) => {
    handleFiltersChange({
      date_range: dateRange,
      start_date: dateRange === 'custom' ? DEFAULT_ORDER_START_DATE : null,
      end_date: null
    });
  }, [handleFiltersChange]);

  // Keep the input responsive while only applying it to the API filter after a pause.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => (
        prev.search === searchInput
          ? prev
          : { ...prev, search: searchInput }
      ));
      setPagination(prev => (
        prev.page === 1
          ? prev
          : { ...prev, page: 1 }
      ));
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
  }, []);

  // Handle page changes
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  // Handle table sorting
  const handleSort = useCallback((headingIndex, direction) => {
    // Map table column index to sort field
    const sortMappings = {
      0: 'order_id',         // Order ID column
      1: 'customer_name',    // Customer column
      2: 'status',           // Status column
      3: null,               // Flags column (not sortable)
      4: null,               // Payment status column (not sortable)
      5: 'total',            // Total column
      6: 'created_at',       // Created column
      7: null                // Actions column (not sortable)
    };

    const sortField = sortMappings[headingIndex];
    if (sortField) {
      const sortValue = `${sortField}_${direction}`;
      setFilters(prev => ({ ...prev, sort: sortValue }));
      setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when sorting
    }
  }, []);



  // Navigate to order detail
  const handleOrderClick = useCallback((orderId) => {
    const country = authService.getActiveCountry();
    navigate(
      `/orders/${encodeURIComponent(orderId)}?country=${encodeURIComponent(country)}`
    );
  }, [navigate]);

  // Handle Shopify order import
  const handleImportOrder = useCallback(async (shopifyOrderId) => {
    try {
      setImportLoading(true);
      setImportError(null);

      const terminal = await ordersService.importShopifyOrder({
        requestId: importRequestId,
        country: importCountry,
        store: importStore,
        shopifyOrderId
      });
      setImportTerminal(terminal);

      setToast({
        content: `Shopify import ${terminal.result.status}`,
        duration: 5000
      });

      // Refresh the orders list to show the new order
      loadOrders();

    } catch (_error) {
      setImportError('Shopify import could not be completed');
    } finally {
      setImportLoading(false);
    }
  }, [
    importCountry,
    importRequestId,
    importStore,
    loadOrders
  ]);

  // Handle import modal close
  const handleImportModalClose = useCallback(() => {
    setImportModalOpen(false);
    setImportError(null);
    setImportTerminal(null);
    setImportRequestId(null);
  }, []);

  // Helper to remove a specific filter value
  const handleRemoveFilter = useCallback((key, valueToRemove) => {
    if (key === 'statuses' || key === 'priorities' || key === 'sources' || key === 'flag_types') {
      // For array filters, remove specific value
      const currentValues = filters[key] || [];
      const newValues = currentValues.filter(v => v !== valueToRemove);
      handleFiltersChange({ [key]: newValues });
    } else {
      // For single value filters, clear the value
      handleFiltersChange({ [key]: '' });
    }
  }, [filters, handleFiltersChange]);

  // Filter components - Enhanced with multi-select and additional filters
  const filterComponents = [
    {
      key: 'statuses',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          allowMultiple
          choices={FILTER_OPTIONS.STATUS}
          selected={filters.statuses}
          onChange={(value) => handleFiltersChange({ statuses: value })}
        />
      ),
      shortcut: true
    },
    {
      key: 'priorities',
      label: 'Priority',
      filter: (
        <ChoiceList
          title="Priority"
          titleHidden
          allowMultiple
          choices={FILTER_OPTIONS.PRIORITY}
          selected={filters.priorities}
          onChange={(value) => handleFiltersChange({ priorities: value })}
        />
      ),
      shortcut: true
    },
    ...(!fixedSource ? [{
      key: 'sources',
      label: 'Order Source',
      filter: (
        <ChoiceList
          title="Order Source"
          titleHidden
          allowMultiple
          choices={FILTER_OPTIONS.SOURCE}
          selected={filters.sources}
          onChange={(value) => handleFiltersChange({ sources: value })}
        />
      ),
      shortcut: true
    }] : []),
    {
      key: 'has_flags',
      label: 'Flags',
      filter: (
        <ChoiceList
          title="Flag Status"
          titleHidden
          choices={FILTER_OPTIONS.HAS_FLAGS}
          selected={filters.has_flags ? [filters.has_flags] : []}
          onChange={(value) => handleFiltersChange({ has_flags: value[0] || '' })}
        />
      )
    },
    {
      key: 'payment_status',
      label: 'Payment Status',
      filter: (
        <ChoiceList
          title="Payment Status"
          titleHidden
          choices={FILTER_OPTIONS.PAYMENT_STATUS}
          selected={filters.payment_status ? [filters.payment_status] : []}
          onChange={(value) => handleFiltersChange({ payment_status: value[0] || '' })}
        />
      )
    }
  ];

  // Build applied filters for Polaris Filters component
  // Note: The 'key' in appliedFilters must match the 'key' of the parent filter for tags to display
  const appliedFilters = [];

  // Status filters - combine all selected statuses into one applied filter
  if (filters.statuses && filters.statuses.length > 0) {
    const statusLabels = filters.statuses.map(s => ORDER_STATUS_LABELS[s] || s).join(', ');
    appliedFilters.push({
      key: 'statuses',
      label: statusLabels,
      onRemove: () => handleFiltersChange({ statuses: [] })
    });
  }

  // Priority filters - combine all selected priorities into one applied filter
  if (filters.priorities && filters.priorities.length > 0) {
    const priorityLabels = filters.priorities.map(p => ORDER_PRIORITY_LABELS[p] || p).join(', ');
    appliedFilters.push({
      key: 'priorities',
      label: priorityLabels,
      onRemove: () => handleFiltersChange({ priorities: [] })
    });
  }

  // Order source filters
  if (!fixedSource && filters.sources && filters.sources.length > 0) {
    const sourceLabels = filters.sources
      .map(source => FILTER_OPTIONS.SOURCE.find(option => option.value === source)?.label || source)
      .join(', ');
    appliedFilters.push({
      key: 'sources',
      label: sourceLabels,
      onRemove: () => handleFiltersChange({ sources: [] })
    });
  }

  // Has flags filter
  if (filters.has_flags) {
    const flagLabel = FILTER_OPTIONS.HAS_FLAGS.find(f => f.value === filters.has_flags)?.label || 'With Flags';
    appliedFilters.push({
      key: 'has_flags',
      label: flagLabel,
      onRemove: () => handleRemoveFilter('has_flags')
    });
  }

  // Payment status filter
  if (filters.payment_status) {
    const paymentLabel = FILTER_OPTIONS.PAYMENT_STATUS.find(f => f.value === filters.payment_status)?.label;
    appliedFilters.push({
      key: 'payment_status',
      label: paymentLabel,
      onRemove: () => handleRemoveFilter('payment_status')
    });
  }

  // Clear all filters
  const handleFiltersClear = useCallback(() => {
    setSearchInput('');
    setSelectedStatKey(null);
    setFilters(createDefaultOrderFilters(fixedSource));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [fixedSource]);

  // Helper to check if any filters are applied
  const hasFiltersApplied = () => {
    return filters.search ||
      filters.statuses.length > 0 ||
      filters.priorities.length > 0 ||
      filters.sources.length > 0 ||
      filters.flag_types.length > 0 ||
      filters.has_flags ||
      filters.payment_method ||
      filters.payment_status ||
      filters.financial_status ||
      (filters.date_range && filters.date_range !== 'all');
  };

  // Empty state
  const emptyStateMarkup = (
    <EmptyState
      heading="No orders found"
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
      action={{
        content: 'Create Order',
        onAction: () => navigate('/orders/new')
      }}
    >
      <p>
        {hasFiltersApplied()
          ? 'Try adjusting your search or filter criteria'
          : 'Get started by creating your first order'}
      </p>
    </EmptyState>
  );

  const tableSortFields = ['order_id', 'customer_name', 'status', null, null, 'total', 'created_at'];
  const activeSortField = filters.sort.split('_').slice(0, -1).join('_');
  const activeSortColumnIndex = tableSortFields.indexOf(activeSortField);

  // Loading state
  if (loading && orders.length === 0) {
    return (
      <Card>
        <div style={{ padding: '60px', textAlign: 'center' }}>
          <Spinner size="large" />
          <Text variant="bodyMd" color="subdued">
            Loading orders...
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <>
      {dateFilterTarget && createPortal(
        <InlineStack gap="200" blockAlign="center">
          <div style={{ minWidth: '170px' }}>
            <Select
              label="Order date"
              labelHidden
              options={FILTER_OPTIONS.DATE_RANGES}
              value={filters.date_range}
              onChange={handleDateRangeChange}
            />
          </div>
          {filters.date_range === 'custom' && (
            <>
              <div style={{ width: '150px' }}>
                <TextField
                  label="Start date"
                  labelHidden
                  type="date"
                  value={filters.start_date || ''}
                  onChange={(value) => handleFiltersChange({ start_date: value })}
                  autoComplete="off"
                />
              </div>
              <div style={{ width: '150px' }}>
                <TextField
                  label="End date"
                  labelHidden
                  type="date"
                  value={filters.end_date || ''}
                  onChange={(value) => handleFiltersChange({ end_date: value })}
                  autoComplete="off"
                />
              </div>
            </>
          )}
        </InlineStack>,
        dateFilterTarget
      )}

      {/* Quick Stats Cards */}
      <QuickStatsCards
        stats={quickStats}
        loading={quickStatsLoading}
        onStatClick={handleStatClick}
        selectedStatKey={selectedStatKey}
      />

      <Card>
        <BlockStack gap="400">
          {/* Sort dropdown above filters */}
          <InlineStack align="end">
            <div style={{ minWidth: '250px' }}>
              <Select
                label="Sort by"
                labelInline
                options={FILTER_OPTIONS.SORT_OPTIONS}
                value={filters.sort}
                onChange={(value) => handleFiltersChange({ sort: value })}
              />
            </div>
          </InlineStack>

          {/* Filters component with enhanced search */}
          <Filters
            queryValue={searchInput}
            queryPlaceholder="Search by order ID, customer, email, or phone..."
            filters={filterComponents}
            appliedFilters={appliedFilters}
            onQueryChange={handleSearchChange}
            onQueryClear={() => handleSearchChange('')}
            onClearAll={handleFiltersClear}
          />

          {/* Results count */}
          {pagination.total > 0 && (
            <InlineStack align="space-between">
              <Text variant="bodyMd" as="p" tone="subdued">
                Showing {orders.length} of {pagination.total} orders
                {hasFiltersApplied() && ' (filtered)'}
              </Text>
            </InlineStack>
          )}
        </BlockStack>

        {orders.length === 0 ? (
          emptyStateMarkup
        ) : (
          <>
            {/* IndexTable for advanced row styling */}
            <IndexTable
              resourceName={{ singular: 'order', plural: 'orders' }}
              itemCount={orders.length}
              selectable={false}
              headings={[
                { title: 'Order ID' },
                { title: 'Customer' },
                { title: 'Status' },
                { title: 'Flags' },
                { title: 'Payment Status' },
                { title: 'Total' },
                { title: 'Created' }
              ]}
              sortable={[true, true, true, false, false, true, true]}
              sortColumnIndex={activeSortColumnIndex >= 0 ? activeSortColumnIndex : 6}
              sortDirection={filters.sort.endsWith('asc') ? 'ascending' : 'descending'}
              onSort={handleSort}
            >
              {orders.map((order, index) => {
                const formattedOrder = ordersService.formatOrderForDisplay(order);
                const orderId = order.order_id || order.id || order._id;
                const sourceDisplay = orderUtils.getSourceDisplay(order);
                const paymentDisplay = getPaymentDisplay(order.payment);
                const isUrgent = order.priority === 'urgent' && order.status !== 'delivered' && order.status !== 'cancelled';

                return (
                  <IndexTable.Row
                    id={orderId}
                    key={orderId}
                    position={index}
                    tone={isUrgent ? 'critical' : undefined}
                    onClick={() => handleOrderClick(orderId)}
                  >
                    <IndexTable.Cell>
                      <Button
                        plain
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOrderClick(orderId);
                        }}
                        textAlign="left"
                      >
                        <InlineStack gap="100" align="start">
                          {isUrgent && (
                            <Text tone="critical">🔥</Text>
                          )}
                          <Text variant="bodyMd" fontWeight="medium" tone={isUrgent ? 'critical' : undefined}>
                            {orderId}
                          </Text>
                          <Badge status={sourceDisplay.color} size="small">
                            {sourceDisplay.label}
                          </Badge>
                        </InlineStack>
                      </Button>
                    </IndexTable.Cell>

                    <IndexTable.Cell>
                      <BlockStack gap="100">
                        <Text variant="bodyMd">{order.customer?.name || 'N/A'}</Text>
                        <Text variant="bodySm" color="subdued">{order.customer?.email || 'N/A'}</Text>
                      </BlockStack>
                    </IndexTable.Cell>

                    <IndexTable.Cell>
                      <Badge
                        status={ORDER_STATUS_COLORS[order.status] || 'subdued'}
                        size="small"
                      >
                        {orderUtils.getStatusLabel(order.status)}
                      </Badge>
                    </IndexTable.Cell>

                    <IndexTable.Cell>
                      {(() => {
                        const unresolvedFlags = order.flags ? order.flags.filter(flag => !flag.resolved) : [];
                        const unresolvedCount = order.unresolved_flags_count !== undefined ? order.unresolved_flags_count : unresolvedFlags.length;

                        return unresolvedCount > 0 ? (
                          <Tooltip
                            content={
                              <BlockStack gap="100">
                                <Text variant="bodyMd" fontWeight="bold">Unresolved Flags ({unresolvedCount}):</Text>
                                {unresolvedFlags.map((flag, idx) => (
                                  <BlockStack key={idx} gap="050">
                                    <Text variant="bodySm" fontWeight="semibold">
                                      {flag.flag_type?.replace(/_/g, ' ').toUpperCase() || 'ISSUE'}
                                    </Text>
                                    <Text variant="bodySm">
                                      {flag.description || 'No description'}
                                    </Text>
                                    {idx < unresolvedFlags.length - 1 && <div style={{ borderTop: '1px solid #e0e0e0', margin: '4px 0' }} />}
                                  </BlockStack>
                                ))}
                              </BlockStack>
                            }
                            dismissOnMouseOut
                            preferredPosition="above"
                          >
                            <InlineStack gap="200">
                              <Badge status="critical" size="small">
                                🚩 {unresolvedCount}
                              </Badge>
                            </InlineStack>
                          </Tooltip>
                        ) : (
                          <Text variant="bodySm" color="subdued">—</Text>
                        );
                      })()}
                    </IndexTable.Cell>

                    <IndexTable.Cell>
                      <Badge
                        status={paymentDisplay.badgeStatus}
                        size="small"
                      >
                        {paymentDisplay.label}
                      </Badge>
                    </IndexTable.Cell>

                    <IndexTable.Cell>
                      <Text variant="bodyMd" fontWeight="medium">
                        {formattedOrder.formattedTotal}
                      </Text>
                    </IndexTable.Cell>

                    <IndexTable.Cell>
                      <BlockStack gap="100">
                        <Text variant="bodySm">{formattedOrder.formattedCreatedAt}</Text>
                        <Text variant="bodySm" color="subdued">
                          Updated: {formattedOrder.formattedUpdatedAt}
                        </Text>
                      </BlockStack>
                    </IndexTable.Cell>

                  </IndexTable.Row>
                );
              })}
            </IndexTable>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '1rem',
              borderTop: '1px solid var(--p-color-border-subdued)'
            }}>
              <Pagination
                hasPrevious={pagination.page > 1}
                onPrevious={() => handlePageChange(pagination.page - 1)}
                hasNext={pagination.page < pagination.totalPages}
                onNext={() => handlePageChange(pagination.page + 1)}
                label={`Page ${pagination.page} of ${pagination.totalPages}`}
              />
            </div>
          </>
        )}
      </Card>

      {toast && (
        <Toast
          content={toast.content}
          error={toast.error}
          onDismiss={() => setToast(null)}
        />
      )}

      <ImportShopifyOrderModal
        isOpen={importModalOpen}
        onClose={handleImportModalClose}
        onImport={handleImportOrder}
        country={importCountry}
        store={importStore}
        terminal={importTerminal}
        loading={importLoading}
        error={importError}
      />
    </>
  );
};

export default OrdersPage;